// AIペルソナ API パス・パラメータ・レスポンス型
// 対応ルーター: backend/routers/persona_admin.py

export const PersonaAdminApiPath = {
  LIST_CATALOG: '/api/admin/persona/catalog',
  GET_READINESS: '/api/admin/persona/readiness',
  IMPORT_CATALOG: '/api/admin/persona/catalog/import',
  GET_CATALOG_DETAIL: '/api/admin/persona/catalog/{template_id}',
  UPDATE_CATALOG: '/api/admin/persona/catalog/{template_id}',
  GET_CATALOG_HISTORY: '/api/admin/persona/catalog/{template_id}/history'
} as const

/** サイト向け（routers/persona.py） */
export const PersonaApiPath = {
  LIST_TEMPLATES: '/api/sites/{site_id}/persona/templates',
  GET_TEMPLATE: '/api/sites/{site_id}/persona/templates/{template_id}',
  ADOPT: '/api/sites/{site_id}/persona/adopt',
  UPDATE: '/api/sites/{site_id}/persona/{persona_id}',
  RESET: '/api/sites/{site_id}/persona/{persona_id}/reset',
  ACTIVATE: '/api/sites/{site_id}/persona/{persona_id}/activate?force={force}',
  DEACTIVATE: '/api/sites/{site_id}/persona/{persona_id}/deactivate',
  GET_STATS: '/api/sites/{site_id}/persona/stats',
  GET_HEALTH: '/api/sites/{site_id}/persona/health',
  GET_TREND: '/api/sites/{site_id}/persona/trend?days={days}',
  GET_CUSTOMER: '/api/sites/{site_id}/persona/customers/{customer_id}',
  GET_HISTORY: '/api/sites/{site_id}/persona/{persona_id}/history',
  GET_MEASUREMENT_CONFIG: '/api/sites/{site_id}/persona/measurement-config',
  UPDATE_MEASUREMENT_CONFIG: '/api/sites/{site_id}/persona/measurement-config',
  LIST_INSIGHTS: '/api/sites/{site_id}/persona/insights',
  RUN_INSIGHT: '/api/sites/{site_id}/persona/insights',
  GET_INSIGHT_SNAPSHOT: '/api/sites/{site_id}/persona/insights/{insight_id}/snapshot'
} as const

export type PersonaDifficultyBand = '1-3' | '4-6' | '7-8' | '9-10'
export type PersonaAcquisitionRoute = 'standard' | 'zero_party' | 'external'

export interface PersonaCatalogItem {
  template_id: string
  name: string
  primary_axis: string | null
  acquisition_route: PersonaAcquisitionRoute
  judge_text: string
  difficulty: number
  contribution: string | null
  tactics: string[]
  avatar_emoji: string | null
  avatar_color: string | null
  version: number
  status: string
  /** 条件式があるか。無いものは判定対象にならない（該当0人とは意味が違う） */
  judgeable: boolean
  conditions: Record<string, unknown> | null
  /** 条件式を1行1条件の日本語にしたもの */
  condition_summary: string[]
  /** 条件式が参照しているメトリクス。ListPersonaCatalogResponse.metrics を引くキー */
  metrics_used: string[]
  /** 原文のうち条件式で表せていない部分。判定できる＝原文どおり、ではない */
  draft_note: string | null
  blocked_by: string[]
  blocked_labels: string[]
}

/** メトリクスの定義。「何をどのテーブルからどう集計しているか」の正 */
export interface PersonaMetricDef {
  metric_key: string
  description: string | null
  axis: string | null
  value_type: string
  window_days: number | null
  source_tables: string[]
  sql_template: string
  availability_sql: string | null
  /** 行が無い顧客に補う値（例：注文0回）。null なら補わず「判定不能」 */
  default_when_missing: number | null
}

export interface ListPersonaCatalogVars {
  difficulty_band?: PersonaDifficultyBand
  judgeable?: boolean
  search?: string
}

export interface ListPersonaCatalogResponse {
  items: PersonaCatalogItem[]
  total: number
  /** 一覧に出たテンプレートが参照しているメトリクスの定義 */
  metrics: PersonaMetricDef[]
}

export interface PersonaDifficultyBandStat {
  band: string
  total: number
  judgeable: number
}

export interface PersonaCapability {
  key: string
  label: string
  why: string
  build: string
  effort: string
  /** その能力**だけ**を作れば判定可能になる体数 */
  unlocks_alone: number
  /** その能力が関わっている体数（他にも足りないものがある場合を含む） */
  involved: number
  templates: string[]
}

export interface PersonaReadinessResponse {
  total: number
  judgeable: number
  blocked: number
  by_difficulty: PersonaDifficultyBandStat[]
  capabilities: PersonaCapability[]
}

// ------------------------------------------------------------------
// サイト向け
// ------------------------------------------------------------------

export interface SitePersonaTemplate {
  template_id: string
  name: string
  primary_axis: string | null
  acquisition_route: PersonaAcquisitionRoute
  judge_text: string
  difficulty: number
  contribution: string | null
  tactics: string[]
  avatar_emoji: string | null
  avatar_color: string | null
  adopted: boolean
  active: boolean
  /** null = 判定不能。0（該当者なし）と混同しないこと */
  matched_size: number | null
  judgeable: boolean
  blocked_metrics: string[]
  /** カタログが改版されている（採用済みのみ） */
  has_new_version: boolean
}

export interface ListSitePersonaVars {
  site_id: number
  difficulty_band?: PersonaDifficultyBand
  adopted?: boolean
  judgeable?: boolean
  search?: string
}

export interface ListSitePersonaResponse {
  items: SitePersonaTemplate[]
  total: number
  adopted: number
  active: number
  /** 有効化に必要な最小の該当人数。未満は force=true（強制で有効化）が必要 */
  min_active_size: number
}

export interface PersonaAxisScore {
  axis: string
  /** null = その軸の属性が1つも無い。0点（低い）ではない */
  score: number | null
  customers: number
}

export interface GetSitePersonaVars {
  site_id: number
  template_id: string
}

/** メトリクスの意味と型。サイト向けにはSQLを出さない（測り方はW2の持ち物） */
export interface PersonaMetricBrief {
  metric_key: string
  description: string | null
  value_type: string
  window_days: number | null
}

/** 条件式のリーフ。閾値エディタが編集する単位 */
export interface PersonaConditionLeaf {
  metric: string
  op: string
  value?: unknown
  /** 'pct' はサイト内の百分位（0-100）と比べる。落とすと「上位10％」が「10円以上」に化ける */
  src?: 'raw' | 'pct'
}

export interface SitePersonaDetailResponse {
  template: SitePersonaTemplate
  conditions: Record<string, unknown> | null
  condition_summary: string[]
  metrics_used: string[]
  radar: PersonaAxisScore[]
  /** 採用済みのときだけ入る */
  site_persona: SitePersonaRow | null
  metrics: PersonaMetricBrief[]
}

export interface SitePersonaRow {
  persona_id: string
  template_id: string | null
  name: string
  judge_text: string | null
  active: boolean
  matched_size: number | null
  priority: number
  measuring_until: string | null
  avatar_emoji: string | null
  avatar_color: string | null
  /** サイトが調整した実行値。カタログの既定と異なることがある */
  conditions: Record<string, unknown> | null
  condition_summary: string[]
  tactics: string[]
  judged_at: string | null
  conditions_updated_at: string | null
  /** 条件を変えた後まだ判定していない。この間 matched_size は前の条件での件数 */
  conditions_stale: boolean
  /** カタログの既定と違う値になっているか */
  customized: boolean
  template_version_adopted: number | null
  template_version: number | null
  /** カタログが改版されている。取り込むかはサイトが選ぶ（自動反映しない） */
  has_new_version: boolean
}

export interface UpdateSitePersonaVars {
  site_id: number
  persona_id: string
  /** 省略した項目は変更しない */
  name?: string
  conditions?: Record<string, unknown>
  tactics?: string[]
  priority?: number
}

export interface ResetSitePersonaVars {
  site_id: number
  persona_id: string
}

export interface AdoptPersonaVars {
  site_id: number
  template_id: string
}

export interface ActivatePersonaVars {
  site_id: number
  persona_id: string
  /** パスに埋め込むため必須。省くと URL に {force} が残る */
  force: boolean
}

export interface DeactivatePersonaVars {
  site_id: number
  persona_id: string
}

export interface PersonaDecidedBy {
  rule: string
  label: string
  customers: number
}

export interface PersonaStatsVars {
  site_id: number
}

export interface PersonaStatsResponse {
  adopted: number
  active: number
  undecidable: number
  customers: number
  matched_customers: number
  matches: number
  assigned: number
  assignment_history: number
  decided_by: PersonaDecidedBy[]
  last_judged_at: string | null
  by_persona: SitePersonaRow[]
}

export interface PersonaTrendVars {
  site_id: number
  /** パスに埋め込むため必須。省くと URL に {days} が残る */
  days: number
}

export interface PersonaTrendSeries {
  persona_id: string
  name: string
  avatar_emoji: string | null
  avatar_color: string | null
  active: boolean
  first: number
  last: number
  delta: number
  /** dates と同じ長さ */
  values: number[]
}

export interface PersonaTransition {
  /** null = 新規割当（それまで誰でもなかった） */
  from_persona: string | null
  from_name: string | null
  /** null = 割当が外れた */
  to_persona: string | null
  to_name: string | null
  customers: number
}

export interface PersonaTrendResponse {
  /** 記録のある日だけ。無い期間は埋めていない */
  dates: string[]
  series: PersonaTrendSeries[]
  transitions: PersonaTransition[]
  total_current: number
  has_history: boolean
  has_transitions: boolean
  note: string | null
}

export interface PersonaAxisCoverage {
  axis: string
  metrics: number
  available: number
  customers_with_any: number
  coverage: number
}

export interface PersonaBlockedMetric {
  metric_key: string
  axis: string | null
  reason: string
}

export interface PersonaHealthResponse {
  computed_at: string | null
  customers: number
  attributes: number
  metrics_total: number
  metrics_available: number
  /** null = 行動ログが無く測れない。0.0（誰も名寄せできていない）ではない */
  identity_rate: number | null
  identity_visitors: number | null
  identity_resolved: number | null
  axis_coverage: PersonaAxisCoverage[]
  blocked_metrics: PersonaBlockedMetric[]
  alerts: string[]
}

export interface PersonaCatalogChange {
  template_id: string
  name: string
  /** created / updated */
  kind: string
  /** 版を上げる原因になった項目 */
  fields: string[]
}

export interface ImportPersonaCatalogVars {
  file: File
  /** 既定は true（取り込まずに差分だけ見る） */
  dry_run: boolean
}

export interface ImportPersonaCatalogResponse {
  dry_run: boolean
  file_name: string
  total: number
  created: number
  /** 中身が変わったので版を上げたもの */
  updated: number
  unchanged: number
  drafted: number
  invalid: string[]
  changes: PersonaCatalogChange[]
}

// ------------------------------------------------------------------
// 定義の編集と変更履歴（openspec: persona-definition-editing）
// ------------------------------------------------------------------

/** 変更履歴の1件。変更前後を並べて読めるようにするための形 */
export interface PersonaDefinitionHistoryRow {
  id: number
  /** catalog = 全体管理のカタログ / site = サイト実体 */
  scope: string
  site_id: number | null
  template_id: string | null
  persona_id: string | null
  name: string | null
  changed_fields: string[]
  before: Record<string, unknown>
  after: Record<string, unknown>
  version: number | null
  operator_id: number | null
  operator_name: string | null
  note: string | null
  created_at: string | null
}

export interface PersonaDefinitionHistoryResponse {
  items: PersonaDefinitionHistoryRow[]
  total: number
}

/** カタログ編集画面が必要とするもの。採用中のサイト数を必ず伴う */
export interface PersonaCatalogDetailResponse {
  catalog: PersonaCatalogItem
  /** そのカタログを採用しているサイト数。共通資産の影響範囲を示す */
  adopted_site_count: number
  history: PersonaDefinitionHistoryRow[]
}

export interface UpdatePersonaCatalogVars {
  template_id: string
  name?: string
  judge_text?: string
  conditions?: Record<string, unknown>
  tactics?: string[]
  avatar_emoji?: string
  avatar_color?: string
  draft_note?: string
  /** 何のために変えたか。履歴を後から読むときの手がかり */
  note?: string
}

export interface PersonaHistoryVars {
  site_id: number
  persona_id: string
}

// ------------------------------------------------------------------
// 効果測定の設定（対照群の比率・測定の窓）
// ------------------------------------------------------------------

/** いまの比率で分けたときのペルソナごとの人数 */
export interface PersonaHoldoutPreviewRow {
  persona_id: string
  name: string
  /** 割当の全体 */
  assigned: number
  /** 実際にW2へ渡す人数 */
  delivered: number
  /** リストに載せない人数 */
  holdout: number
  /** 対照群が小さすぎて uplift を出せない。人数だけでは判断できないので受け取る */
  too_small: boolean
}

export interface PersonaMeasurementConfigHistoryRow {
  holdout_rate: number
  window_days: number
  hash_version: number
  use_uplift_in_ranking: boolean
  valid_from: string | null
  /** null = 現行 */
  valid_to: string | null
  operator_name: string | null
  note: string | null
  created_at: string | null
}

/** 直近の測定結果。**null は「測れなかった」で 0 ではない** */
export interface PersonaUpliftBriefRow {
  persona_id: string
  name: string
  /** 購入率の差（0.05 = 5ポイント）。null = 測れなかった */
  uplift_cvr: number | null
  /** 1人あたり売上の差（円）。null = 測れなかった */
  uplift_arpu: number | null
  z_score: number | null
  /** 順位づけに使えるか。偽なら値が出ていても 0 として扱われる */
  reliable: boolean
  /** 測れない・弱い理由。値が出ないときは必ず入る */
  reason: string | null
  measured_from: string | null
  measured_to: string | null
  /** promotion = 施策の実行日 / handover = リストを渡した日（暫定） */
  source: string | null
  delivered_n: number
  holdout_n: number
}

export interface PersonaMeasurementConfigResponse {
  holdout_rate: number
  window_days: number
  hash_version: number
  /** 実測 uplift を主ペルソナの順位づけに使うか。既定は false */
  use_uplift_in_ranking: boolean
  /** まだ一度も設定していない（既定値で動いている）。0% とは違う */
  is_default: boolean
  valid_from: string | null
  /** 入力できる範囲。画面側にハードコードしない */
  max_holdout_rate: number
  min_window_days: number
  max_window_days: number
  /** 対照群がこの人数を下回ると uplift を出さない */
  min_holdout_size: number
  preview: PersonaHoldoutPreviewRow[]
  /** 直近の測定結果。順位づけを入れる前に何が起きるかを見せる */
  uplift: PersonaUpliftBriefRow[]
  history: PersonaMeasurementConfigHistoryRow[]
}

export interface UpdatePersonaMeasurementConfigVars {
  site_id: number
  holdout_rate: number
  window_days: number
  use_uplift_in_ranking: boolean
  note?: string
}

// ------------------------------------------------------------------
// 状況示唆（AI）
// ------------------------------------------------------------------

/** 4問への答え */
export interface PersonaInsightFinding {
  /** bias = 偏り / movement = 動き / discovery = 気づき / operation = 運用 */
  question: string | null
  title: string | null
  body: string | null
  personas: string[]
}

/** 運用の提案。**施策ではない**（販促はプロモーションカルテの領分） */
export interface PersonaInsightOperation {
  /** activate / deactivate / priority / measure / consolidate */
  action: string | null
  persona_ids: string[]
  reason: string | null
}

export interface PersonaInsightBody {
  summary: string | null
  findings: PersonaInsightFinding[]
  operations: PersonaInsightOperation[]
  /** データが無くて触れられなかったこと */
  not_covered: string[]
}

/** 出力の検証結果。空でも「検証していない」とは違う */
export interface PersonaInsightWarning {
  /** unknown_number / unknown_persona / unavailable_source / near_token_limit */
  kind: string
  message: string
  detail?: unknown
}

export interface PersonaInsightRow {
  id: number
  generation: number
  days: number
  output: PersonaInsightBody
  warnings: PersonaInsightWarning[]
  model: string | null
  tokens_in: number | null
  tokens_out: number | null
  operator_name: string | null
  created_at: string | null
}

export interface PersonaInsightListResponse {
  items: PersonaInsightRow[]
  /** 保存できる世代数 */
  keep_generations: number
}

export interface PersonaInsightRunResponse {
  ok: boolean
  insight: PersonaInsightRow | null
  /** 古い世代を落とした件数 */
  dropped: number
  error: string | null
}

export interface RunPersonaInsightVars {
  site_id: number
  days: number
}
